import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckSquare, ArrowRight, Calendar, AlertCircle, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { tasksApi } from '@/lib/api';
import { cn } from '@/lib/utils';

interface MyTasksCardProps {
  maxTasks?: number;
}

const priorityConfig = {
  URGENT: { color: 'text-red-600 bg-red-100', icon: AlertCircle },
  HIGH: { color: 'text-orange-600 bg-orange-100', icon: AlertCircle },
  MEDIUM: { color: 'text-blue-600 bg-blue-100', icon: null },
  LOW: { color: 'text-grey-500 bg-grey-100', icon: null },
};

function formatDueDate(dateString: string): { text: string; isOverdue: boolean; isDueSoon: boolean } {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(date);
  dueDate.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { text: `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} overdue`, isOverdue: true, isDueSoon: false };
  } else if (diffDays === 0) {
    return { text: 'Due today', isOverdue: false, isDueSoon: true };
  } else if (diffDays === 1) {
    return { text: 'Due tomorrow', isOverdue: false, isDueSoon: true };
  } else if (diffDays <= 7) {
    return { text: `Due in ${diffDays} days`, isOverdue: false, isDueSoon: true };
  } else {
    return { text: date.toLocaleDateString(), isOverdue: false, isDueSoon: false };
  }
}

export function MyTasksCard({ maxTasks = 5 }: MyTasksCardProps) {
  const { data: tasks, isLoading } = useQuery({
    queryKey: ['myTasks', 'incomplete'],
    queryFn: () => tasksApi.getMyTasks({ status: 'incomplete', limit: maxTasks + 1 }),
    staleTime: 30000,
  });

  const displayedTasks = tasks?.slice(0, maxTasks) || [];
  const hasMore = (tasks?.length || 0) > maxTasks;
  const totalCount = tasks?.length || 0;

  return (
    <Card padding="none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-grey-100 bg-grey-50/50">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-success" />
          <span className="text-sm font-medium text-secondary">My Tasks</span>
        </div>
        {!isLoading && <span className="text-xs text-grey-400">({totalCount})</span>}
      </div>

      {isLoading ? (
        <div className="px-4 py-8 flex justify-center">
          <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
        </div>
      ) : displayedTasks.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <CheckSquare className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-sm text-grey-500">No tasks assigned to you</p>
        </div>
      ) : (
        <div className="divide-y divide-grey-100">
          {displayedTasks.map((task) => {
            const priority = priorityConfig[task.priority as keyof typeof priorityConfig] || priorityConfig.MEDIUM;
            const dueInfo = task.dueDate ? formatDueDate(task.dueDate) : null;
            const PriorityIcon = priority.icon;

            return (
              <Link
                key={task.id}
                to={`/projects/${task.list.project.id}/tasks/${task.list.id}?taskId=${task.id}`}
                className="block p-4 hover:bg-grey-50 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5',
                    task.status === 'COMPLETED' ? 'bg-success border-success' : 'border-grey-300'
                  )}>
                    {task.status === 'COMPLETED' && (
                      <CheckSquare className="w-3 h-3 text-white" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-secondary group-hover:text-accent transition-colors truncate">
                      {task.title}
                    </h3>
                    <p className="text-xs text-grey-500 truncate mt-0.5">
                      {task.list.project.name} / {task.list.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {/* Priority badge */}
                      <span className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
                        priority.color
                      )}>
                        {PriorityIcon && <PriorityIcon className="w-3 h-3" />}
                        {task.priority}
                      </span>

                      {/* Due date */}
                      {dueInfo && (
                        <span className={cn(
                          'inline-flex items-center gap-1 text-xs',
                          dueInfo.isOverdue ? 'text-error font-medium' : dueInfo.isDueSoon ? 'text-warning' : 'text-grey-500'
                        )}>
                          <Calendar className="w-3 h-3" />
                          {dueInfo.text}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="px-4 py-3 border-t border-grey-100">
          <Link
            to="/tasks"
            className="text-sm font-medium text-accent hover:text-accent-700 flex items-center gap-1"
          >
            View all tasks <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </Card>
  );
}
